// Cột sáng nhặt đồ (ITEMS §9, UI_SPEC §12) và phần tranh của nghi thức Khai Bảo. Vẽ bằng Graphics
// thường (không texture lớn, không blend lạ): cột = vài dải chữ nhật mờ dần, cao theo phẩm; Thiên+ có
// bụi bay ngược lên. Khai Bảo: màn tối → rương mực mở → món xoay một vòng trong cột sáng.
import { Container, Graphics, Sprite, Texture, type Application } from 'pixi.js';
import { QUALITIES } from '@ui/tokens';
import { FRAME_H, FRAME_W } from './stage';

const hex = (rgb: string): number => {
  const [r, g, b] = rgb.split(',').map(Number) as [number, number, number];
  return (r << 16) | (g << 8) | b;
};

/** Chiều cao cột theo bậc phẩm (0 Phàm: không cột). */
const BEAM_H = [0, 90, 130, 190, 300, 420, 520];
const BEAM_MS = 1100;

interface Beam {
  g: Graphics;
  dust: Graphics | null;
  t: number;
  lv: number;
}

export interface LootFxLayer {
  beam(rarityLv: number, x?: number): void;
  reveal: {
    /** `reduced` (Giảm chuyển động): không xoay, không mở nắp, không nhấp nhô; hiện thẳng món trong cột. */
    start(art: string | null, rarityLv: number, reduced?: boolean): void;
    /** 0 tối · 1 rương mở · 2 món xoay · 3 đứng yên chờ nhận. */
    skip(): void;
    stop(): void;
    readonly phase: number;
  };
}

function drawColumn(g: Graphics, color: number, h: number, w: number): void {
  g.clear();
  for (let i = 0; i < 3; i++) {
    const k = 1 - i / 3;
    g.beginFill(color, 0.08 + 0.1 * (1 - k));
    g.drawRect((-w * k) / 2, -h * (0.4 + 0.6 * k), w * k, h * (0.4 + 0.6 * k));
    g.endFill();
  }
  g.beginFill(0xffffff, 0.35);
  g.drawRect(-1.5, -h, 3, h);
  g.endFill();
}

export function mountLootFx(app: Application): LootFxLayer {
  const root = new Container();
  app.stage.addChild(root);
  const beams: Beam[] = [];

  // Khai Bảo
  const rite = new Container();
  rite.visible = false;
  root.addChild(rite);
  const dark = new Graphics();
  dark.beginFill(0x000000, 1);
  dark.drawRect(0, 0, FRAME_W, FRAME_H);
  dark.endFill();
  const riteBeam = new Graphics();
  const chest = new Graphics();
  const lid = new Graphics();
  // Món trong cột sáng: khung bọc để xoay (scale.x) mà không đụng kích thước ảnh.
  const item = new Container();
  const itemArt = new Sprite(Texture.EMPTY);
  itemArt.anchor.set(0.5);
  item.addChild(itemArt);
  rite.addChild(dark, riteBeam, chest, lid, item);
  let riteT = 0;
  let riteLv = 5;
  let riteReduced = false;
  let phase = -1;
  // Sau 0,4s màn đã tối hẳn: ẩn mọi lớp khác của stage và bỏ tấm phủ, chỉ còn cột + rương + món trên nền
  // tối của renderer (tô đè cả màn bằng lớp mờ là nút cổ chai fill-rate trên GPU yếu, DECISIONS M1).
  const hidden: Container[] = [];
  const hideOthers = (): void => {
    for (const c of app.stage.children)
      if (c !== root && c.visible) {
        c.visible = false;
        hidden.push(c as Container);
      }
    dark.visible = false;
  };
  const showOthers = (): void => {
    for (const c of hidden.splice(0)) c.visible = true;
    dark.visible = true;
  };

  const tick = (): void => {
    const dt = Math.min(app.ticker.deltaMS, 50);
    for (let i = beams.length - 1; i >= 0; i--) {
      const b = beams[i]!;
      b.t += dt;
      const p = b.t / BEAM_MS;
      b.g.alpha = p < 0.15 ? p / 0.15 : Math.max(0, 1 - (p - 0.15) / 0.85);
      b.g.scale.y = Math.min(1, 0.3 + p * 2);
      if (b.dust) {
        b.dust.y = -p * 120;
        b.dust.alpha = b.g.alpha;
      }
      if (p >= 1) {
        b.g.destroy();
        b.dust?.destroy();
        beams.splice(i, 1);
      }
    }
    if (phase >= 0) {
      riteT += dt;
      const t = riteT / 1000;
      dark.alpha = Math.min(1, t / 0.4);
      if (phase === 0 && t >= 0.4) {
        phase = 1;
        hideOthers();
      }
      if (phase === 1) {
        const k = Math.min(1, (t - 0.4) / 0.6);
        lid.y = -40 * k;
        lid.rotation = -0.5 * k;
        riteBeam.alpha = k;
        if (k >= 1) phase = 2;
      }
      if (phase === 2) {
        const k = Math.min(1, (t - 1.0) / 1.0);
        item.alpha = k;
        item.y = FRAME_H * 0.46 - 50 * k;
        item.scale.set(1.4 * Math.cos(k * Math.PI * 2), 1.4);
        if (k >= 1) phase = 3;
      }
      if (phase === 3) {
        item.scale.set(1.4);
        item.y = FRAME_H * 0.46 - 50 + (riteReduced ? 0 : Math.sin(t * 2) * 3);
      }
    }
  };
  app.ticker.add(tick);

  const setupRite = (art: string | null, lv: number): void => {
    const color = hex(QUALITIES[lv]?.rgb ?? QUALITIES[5]!.rgb);
    drawColumn(riteBeam, color, 560, 90);
    riteBeam.position.set(FRAME_W / 2, FRAME_H * 0.56);
    chest.clear();
    chest.beginFill(0x0b0f0d, 1).lineStyle(2, color, 0.8).drawRoundedRect(-44, -22, 88, 44, 6).endFill();
    chest.position.set(FRAME_W / 2, FRAME_H * 0.56);
    lid.clear();
    lid.beginFill(0x0b0f0d, 1).lineStyle(2, color, 0.8).drawRoundedRect(-46, -10, 92, 16, 5).endFill();
    lid.position.set(FRAME_W / 2 - 30, FRAME_H * 0.56 - 26);
    lid.pivot.set(-30, 0);
    const tex = art ? Texture.from(art) : Texture.WHITE;
    itemArt.texture = tex;
    itemArt.tint = art ? 0xffffff : color;
    const fit = (): void => {
      const side = art ? 72 : 36;
      const k = side / Math.max(1, tex.width, tex.height);
      itemArt.scale.set(k);
    };
    if (tex.baseTexture.valid) fit();
    else tex.baseTexture.once('loaded', fit);
    item.position.set(FRAME_W / 2, FRAME_H * 0.46);
  };

  /** Nhảy tới cảnh cuối (tua, hoặc Giảm chuyển động): màn tối, nắp mở, món đứng trong cột. */
  const jumpToEnd = (): void => {
    riteT = 2000;
    phase = 3;
    dark.alpha = 1;
    hideOthers();
    riteBeam.alpha = 1;
    item.alpha = 1;
    item.scale.set(1.4);
    item.y = FRAME_H * 0.46 - 50;
    lid.y = -40;
    lid.rotation = -0.5;
  };

  return {
    beam(lv, x = FRAME_W / 2) {
      // Scene đang dừng (cửa sổ / trang con phủ lên): bỏ cột, không xếp hàng để diễn trễ khi mở lại
      // (chuông và chấm bay DOM vẫn chạy, DECISIONS M2).
      if (lv <= 0 || beams.length > 8 || !app.ticker.started) return;
      const color = hex(QUALITIES[lv]?.rgb ?? QUALITIES[1]!.rgb);
      const g = new Graphics();
      drawColumn(g, color, BEAM_H[lv] ?? 120, 26 + lv * 4);
      g.position.set(x, FRAME_H * 0.62);
      root.addChildAt(g, 0);
      let dust: Graphics | null = null;
      if (lv >= 4) {
        dust = new Graphics();
        for (let i = 0; i < 14; i++)
          dust
            .beginFill(color, 0.9)
            .drawCircle(((i * 37) % 30) - 15, -((i * 53) % 200), 1.4)
            .endFill();
        dust.position.set(x, FRAME_H * 0.62);
        root.addChild(dust);
      }
      beams.push({ g, dust, t: 0, lv });
    },
    reveal: {
      start(art, lv, reduced = false) {
        riteLv = lv;
        riteReduced = reduced;
        setupRite(art, riteLv);
        riteT = 0;
        phase = 0;
        dark.alpha = 0;
        riteBeam.alpha = 0;
        item.alpha = 0;
        lid.y = 0;
        lid.rotation = 0;
        rite.visible = true;
        if (reduced) jumpToEnd();
      },
      skip() {
        if (phase < 0) return;
        jumpToEnd();
      },
      stop() {
        phase = -1;
        rite.visible = false;
        showOthers();
      },
      get phase() {
        return phase;
      },
    },
  };
}
